import { useCallback, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { clsx } from 'clsx'

import { InquiryDialog } from './inquiry'
import { formatSessionLabel } from './BriefingApplicationModal'
import { lcMarketingApi, type BriefingSession } from '@/api/lcMarketing'
import {
  requestOtp,
  verifyOtp,
  listMyBriefings,
  cancelBriefing,
  changeSession,
  type BriefingIntakeView,
} from '@/api/lcCrm'

function getApiErrorCode(error: unknown): string | undefined {
  return (
    error as {
      response?: { data?: { error?: { code?: string } | null } }
    }
  )?.response?.data?.error?.code
}

type Step = 'phone' | 'otp' | 'list'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function BriefingApplicationManagerModal({ open, onOpenChange }: Props) {
  const { t } = useTranslation('common')
  const queryClient = useQueryClient()

  const [step, setStep] = useState<Step>('phone')
  const [phone, setPhone] = useState('')
  const [otpCode, setOtpCode] = useState('')
  const [sessionToken, setSessionToken] = useState<string | null>(null)

  const handleClose = useCallback(() => {
    setStep('phone')
    setPhone('')
    setOtpCode('')
    setSessionToken(null)
    onOpenChange(false)
  }, [onOpenChange])

  const requestOtpMutation = useMutation({
    mutationFn: () => requestOtp({ phone: phone.replace(/-/g, '') }),
    onSuccess: () => {
      toast.success(t('crm.otp.codeSent'))
      setStep('otp')
    },
    onError: (error) => {
      const code = getApiErrorCode(error)
      if (code) {
        toast.error(t(`crm.apiError.${code}`, { defaultValue: code }))
      }
    },
  })

  const verifyOtpMutation = useMutation({
    mutationFn: () =>
      verifyOtp({ phone: phone.replace(/-/g, ''), code: otpCode }),
    onSuccess: (data) => {
      toast.success(t('crm.otp.verified'))
      setSessionToken(data.sessionToken)
      setStep('list')
    },
    onError: (error) => {
      const code = getApiErrorCode(error)
      if (code) {
        toast.error(t(`crm.apiError.${code}`, { defaultValue: code }))
      }
    },
  })

  const briefingsQuery = useQuery({
    queryKey: ['crm-my-briefings', sessionToken],
    queryFn: () => listMyBriefings(sessionToken!),
    enabled: step === 'list' && !!sessionToken,
  })

  const sessionsQuery = useQuery({
    queryKey: ['lc-marketing', 'briefing-sessions'],
    queryFn: lcMarketingApi.getBriefingSessions,
    enabled: step === 'list',
  })

  const changeSessionMutation = useMutation({
    mutationFn: ({ publicId, sessionId }: { publicId: string; sessionId: string }) =>
      changeSession(sessionToken!, publicId, { sessionId }),
    onSuccess: () => {
      toast.success(t('crm.briefing.sessionChanged'))
      queryClient.invalidateQueries({ queryKey: ['crm-my-briefings'] })
      // 정원이 바뀌므로 공개 회차 목록도 다시 읽는다
      queryClient.invalidateQueries({ queryKey: ['lc-marketing', 'briefing-sessions'] })
    },
    onError: (error) => {
      const code = getApiErrorCode(error)
      if (code) {
        toast.error(t(`crm.apiError.${code}`, { defaultValue: code }))
      }
    },
  })

  const cancelMutation = useMutation({
    mutationFn: (publicId: string) => cancelBriefing(sessionToken!, publicId),
    onSuccess: () => {
      toast.success(t('crm.briefing.cancelled'))
      queryClient.invalidateQueries({ queryKey: ['crm-my-briefings'] })
      queryClient.invalidateQueries({ queryKey: ['lc-marketing', 'briefing-sessions'] })
    },
    onError: (error) => {
      const code = getApiErrorCode(error)
      if (code) {
        toast.error(t(`crm.apiError.${code}`, { defaultValue: code }))
      }
    },
  })

  return (
    <InquiryDialog
      open={open}
      onClose={handleClose}
      title={step === 'list' ? t('crm.briefing.title') : t('crm.otp.title')}
    >
      <div className="flex flex-col gap-4 p-4">
        {step === 'phone' && (
          <PhoneStep
            phone={phone}
            onPhoneChange={setPhone}
            onSubmit={() => requestOtpMutation.mutate()}
            loading={requestOtpMutation.isPending}
            t={t}
          />
        )}

        {step === 'otp' && (
          <OtpStep
            otpCode={otpCode}
            onOtpChange={setOtpCode}
            onVerify={() => verifyOtpMutation.mutate()}
            onResend={() => requestOtpMutation.mutate()}
            loading={verifyOtpMutation.isPending}
            resending={requestOtpMutation.isPending}
            t={t}
          />
        )}

        {step === 'list' && (
          <BriefingListStep
            briefings={briefingsQuery.data ?? []}
            sessions={sessionsQuery.data ?? []}
            loading={briefingsQuery.isLoading}
            onCancel={(publicId) => {
              if (window.confirm(t('crm.briefing.confirmCancel'))) {
                cancelMutation.mutate(publicId)
              }
            }}
            onChangeSession={(publicId, sessionId) =>
              changeSessionMutation.mutate({ publicId, sessionId })
            }
            cancelLoading={cancelMutation.isPending}
            changeLoading={changeSessionMutation.isPending}
            t={t}
          />
        )}
      </div>
    </InquiryDialog>
  )
}

function PhoneStep({
  phone,
  onPhoneChange,
  onSubmit,
  loading,
  t,
}: {
  phone: string
  onPhoneChange: (v: string) => void
  onSubmit: () => void
  loading: boolean
  t: (key: string) => string
}) {
  return (
    <>
      <label className="text-sm font-medium">{t('crm.otp.phoneLabel')}</label>
      <input
        type="tel"
        className="w-full rounded-lg border px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
        placeholder={t('crm.otp.phonePlaceholder')}
        value={phone}
        onChange={(e) => onPhoneChange(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && onSubmit()}
      />
      <button
        type="button"
        onClick={onSubmit}
        disabled={loading || phone.replace(/-/g, '').length < 10}
        className={clsx(
          'w-full rounded-lg py-3 text-sm font-semibold text-white transition',
          'bg-amber-500 hover:bg-amber-600 disabled:opacity-50',
        )}
      >
        {loading ? '...' : t('crm.otp.requestButton')}
      </button>
    </>
  )
}

function OtpStep({
  otpCode,
  onOtpChange,
  onVerify,
  onResend,
  loading,
  resending,
  t,
}: {
  otpCode: string
  onOtpChange: (v: string) => void
  onVerify: () => void
  onResend: () => void
  loading: boolean
  resending: boolean
  t: (key: string) => string
}) {
  return (
    <>
      <label className="text-sm font-medium">{t('crm.otp.codeLabel')}</label>
      <input
        type="text"
        inputMode="numeric"
        maxLength={6}
        className="w-full rounded-lg border px-4 py-3 text-center text-lg tracking-widest focus:outline-none focus:ring-2 focus:ring-amber-400"
        placeholder={t('crm.otp.codePlaceholder')}
        value={otpCode}
        onChange={(e) => onOtpChange(e.target.value.replace(/\D/g, ''))}
        onKeyDown={(e) => e.key === 'Enter' && onVerify()}
      />
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onResend}
          disabled={resending}
          className="flex-1 rounded-lg border py-3 text-sm font-medium transition hover:bg-gray-50 disabled:opacity-50"
        >
          {resending ? '...' : t('crm.otp.resendButton')}
        </button>
        <button
          type="button"
          onClick={onVerify}
          disabled={loading || otpCode.length < 6}
          className={clsx(
            'flex-1 rounded-lg py-3 text-sm font-semibold text-white transition',
            'bg-amber-500 hover:bg-amber-600 disabled:opacity-50',
          )}
        >
          {loading ? '...' : t('crm.otp.verifyButton')}
        </button>
      </div>
    </>
  )
}

function BriefingListStep({
  briefings,
  sessions,
  loading,
  onCancel,
  onChangeSession,
  cancelLoading,
  changeLoading,
  t,
}: {
  briefings: BriefingIntakeView[]
  sessions: BriefingSession[]
  loading: boolean
  onCancel: (publicId: string) => void
  onChangeSession: (publicId: string, sessionId: string) => void
  cancelLoading: boolean
  changeLoading: boolean
  t: (key: string) => string
}) {
  if (loading) return <div className="py-8 text-center text-gray-400">...</div>
  if (briefings.length === 0) {
    return (
      <div className="py-8 text-center text-gray-400">
        {t('crm.briefing.empty')}
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-3">
      {briefings.map((b) => (
        <BriefingRow
          key={b.publicId}
          briefing={b}
          sessions={sessions}
          onCancel={onCancel}
          onChangeSession={onChangeSession}
          cancelLoading={cancelLoading}
          changeLoading={changeLoading}
          t={t}
        />
      ))}
    </div>
  )
}

function BriefingRow({
  briefing,
  sessions,
  onCancel,
  onChangeSession,
  cancelLoading,
  changeLoading,
  t,
}: {
  briefing: BriefingIntakeView
  sessions: BriefingSession[]
  onCancel: (publicId: string) => void
  onChangeSession: (publicId: string, sessionId: string) => void
  cancelLoading: boolean
  changeLoading: boolean
  t: (key: string) => string
}) {
  const [picked, setPicked] = useState<string | null>(null)
  const active = briefing.applicationStatus === 'ACTIVE'
  // 정원이 찬 회차는 옮겨갈 수 없다. 지금 신청한 회차는 비교를 위해 남긴다.
  const selectable = sessions.filter(
    (s) => s.applicationAvailability === 'open' || s.sessionId === briefing.sessionId,
  )
  // 지난 회차에 신청한 경우 현재 회차가 목록에 없다. 그때 첫 항목이 자동 선택되면
  // 고르지도 않은 회차로 바꿀 수 있게 되므로, 빈 값을 두고 직접 고르게 한다.
  const currentIsSelectable = selectable.some((s) => s.sessionId === briefing.sessionId)
  const selected = picked ?? (currentIsSelectable ? briefing.sessionId! : '')
  const changed = !!selected && selected !== briefing.sessionId

  return (
    <div className="rounded-lg border p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium">
            {briefing.sessionDate ?? '-'}{' '}
            <span className="text-gray-500">
              {briefing.sessionMode === 'online'
                ? t('crm.briefing.modeOnline')
                : t('crm.briefing.modeOffline')}
            </span>
          </p>
          <p className="mt-1 text-xs text-gray-400">
            {active
              ? t('crm.briefing.statusActive')
              : t('crm.briefing.statusCancelled')}
          </p>
        </div>
        {active && (
          <button
            type="button"
            onClick={() => onCancel(briefing.publicId)}
            disabled={cancelLoading}
            className="rounded-lg border px-3 py-1.5 text-xs font-medium text-red-500 transition hover:bg-red-50 disabled:opacity-50"
          >
            {t('crm.briefing.cancel')}
          </button>
        )}
      </div>

      {active && selectable.length > 0 && (
        <div className="mt-3 flex gap-2">
          <label className="sr-only" htmlFor={`session-${briefing.publicId}`}>
            {t('crm.briefing.selectSession')}
          </label>
          <select
            id={`session-${briefing.publicId}`}
            value={selected}
            onChange={(e) => setPicked(e.target.value)}
            className="flex-1 rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
          >
            {!currentIsSelectable && (
              <option value="">{t('crm.briefing.selectSession')}</option>
            )}
            {selectable.map((s) => (
              <option key={s.sessionId} value={s.sessionId}>
                {formatSessionLabel(s.startsAt)}{' '}
                {s.mode === 'online'
                  ? t('crm.briefing.modeOnline')
                  : t('crm.briefing.modeOffline')}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => onChangeSession(briefing.publicId, selected)}
            disabled={!changed || changeLoading}
            className={clsx(
              'rounded-lg px-4 py-2 text-xs font-semibold text-white transition',
              'bg-amber-500 hover:bg-amber-600 disabled:opacity-50',
            )}
          >
            {changeLoading ? '...' : t('crm.briefing.changeSession')}
          </button>
        </div>
      )}
    </div>
  )
}
